import type { components } from "../../api/generated-schema";

export type PublicDashboardData = components["schemas"]["PublicDashboardData"];
type CemeteryAnonymousVisitData = components["schemas"]["CemeteryAnonymousVisitData"];

export class PublicDashboardError extends Error {
  constructor(readonly requestId?: string) {
    super("Không tải được số liệu công khai");
    this.name = "PublicDashboardError";
  }
}

type PublicEnvelope = { success?: unknown; data?: unknown; requestId?: unknown };

async function request(path: string, method: "GET" | "POST", signal: AbortSignal): Promise<Record<string, unknown>> {
  const response = await fetch(path, {
    method,
    credentials: "omit",
    headers: {
      Accept: "application/json, application/problem+json",
      "X-Request-Id": crypto.randomUUID(),
    },
    signal,
  });
  let body: unknown;
  try {
    body = await response.json();
  } catch (error) {
    if (signal.aborted) throw error;
    throw new PublicDashboardError(response.headers.get("X-Request-Id") ?? undefined);
  }
  const envelope = body as PublicEnvelope | null;
  const requestId = typeof envelope?.requestId === "string"
    ? envelope.requestId : response.headers.get("X-Request-Id") ?? undefined;
  if (!response.ok || envelope?.success !== true || typeof envelope.data !== "object"
    || envelope.data === null || Array.isArray(envelope.data)) {
    throw new PublicDashboardError(requestId);
  }
  return envelope.data as Record<string, unknown>;
}

export async function fetchPublicDashboard(signal: AbortSignal): Promise<PublicDashboardData> {
  const data = await request("/api/public/dashboard", "GET", signal);
  const { cemeteryCount, graveCount, totalVisits, dailyIncenseCount, statisticsDate, updatedAt, simulated } = data;
  if (![cemeteryCount, graveCount, totalVisits, dailyIncenseCount].every(
    (count) => typeof count === "number" && Number.isSafeInteger(count) && count >= 0,
  ) || typeof statisticsDate !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(statisticsDate)
    || Number.isNaN(Date.parse(statisticsDate))
    || new Date(statisticsDate).toISOString().slice(0, 10) !== statisticsDate
    || typeof updatedAt !== "string" || !/T.*(?:Z|[+-]\d{2}:\d{2})$/.test(updatedAt)
    || Number.isNaN(Date.parse(updatedAt)) || typeof simulated !== "boolean") {
    throw new PublicDashboardError();
  }
  return data as PublicDashboardData;
}

export async function recordPublicCemeteryVisit(cemeterySlug: string, signal: AbortSignal): Promise<CemeteryAnonymousVisitData> {
  const data = await request(`/api/public/cemeteries/${encodeURIComponent(cemeterySlug)}/visits`, "POST", signal);
  if (typeof data.recorded !== "boolean") throw new PublicDashboardError();
  return { recorded: data.recorded };
}
